import { useState } from "react";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { CoverageGap, CoverageGapMember } from "@shared/types/corpus/api.ts";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { usePopupMenu } from "../../../../components/primitives/navigation/PopupMenu";
import { gaps as spacing } from "../../../../components/primitives/spacing";
import { formatDate } from "../../../../lib/date";
import { ChooseCoverageGapDialog } from "./ChooseCoverageGapDialog";
import type { UseCoverageGapResult } from "./useCoverageGap";

interface MemberActionsProps {
  onMove: () => void;
  onDetach: () => void;
}

function MemberActions({ onMove, onDetach }: MemberActionsProps) {
  const { menu, setPopupMenuAnchor } = usePopupMenu([
    [
      { label: "Move to another gap…", onClick: onMove },
      { label: "Take out of this gap", onClick: onDetach },
    ],
  ]);
  return (
    <>
      <IconButton size="small" onClick={setPopupMenuAnchor} aria-label="Claim actions">
        <MoreVertIcon />
      </IconButton>
      {menu}
    </>
  );
}

export interface CoverageGapClaimsProps {
  gap: CoverageGap;
  detail: UseCoverageGapResult;
}

/** The claims of a coverage gap, each with the means to move it to another gap or take it out. */
export function CoverageGapClaims({ gap, detail }: CoverageGapClaimsProps) {
  const { members, openGaps, moveClaim, moveClaimToNewGap, detachClaim } = detail;
  const [moving, setMoving] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function run(action: () => Promise<void>) {
    setActionError(null);
    try {
      await action();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Something went wrong");
    }
  }

  const columns: DataSequenceColumn<CoverageGapMember>[] = [
    {
      key: "claim",
      header: "Claim",
      width: 420,
      cardRole: "primary",
      cell: (member) => (
        <Stack direction="row" gap={spacing.snug} sx={{ alignItems: "center" }}>
          <Typography variant="body2">{member.claim.canonical}</Typography>
          {member.uncovered ? (
            gap.closure?.reason === "filled" ? (
              <Chip size="small" color="warning" label="Still unanswered" />
            ) : null
          ) : (
            <Chip size="small" label="Answered" />
          )}
        </Stack>
      ),
    },
    {
      key: "askers",
      header: "People",
      align: "right",
      cell: (member) => member.demand.askers.toLocaleString(),
    },
    {
      key: "asked",
      header: "Asked",
      align: "right",
      cell: (member) => member.demand.timesAsked.toLocaleString(),
    },
    {
      key: "last",
      header: "Last asked",
      cell: (member) => formatDate(member.demand.lastAskedAt),
    },
    {
      key: "action",
      header: "",
      cardRole: "action",
      cell: (member) => (
        <MemberActions
          onMove={() => setMoving(member.claim.id)}
          onDetach={() => void run(() => detachClaim(member.claim.id))}
        />
      ),
    },
  ];

  return (
    <>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {members.loading ? (
        <LoadingState />
      ) : members.error ? (
        <Alert severity="error">{members.error}</Alert>
      ) : (
        <DataSequence
          columns={columns}
          rows={members.items}
          getRowId={(member) => member.claim.id}
          pagination={members.pagination}
          emptyMessage="No claim in this gap yet. Add some from the uncovered claims."
        />
      )}
      <ChooseCoverageGapDialog
        key={moving ?? "closed"}
        open={moving !== null}
        onClose={() => setMoving(null)}
        title="Move claim to another gap"
        gaps={openGaps}
        excludeGapId={gap.id}
        onChoose={async (gapId) => {
          if (moving) await moveClaim(moving, gapId);
        }}
        onCreate={async (name, note) => {
          if (moving) await moveClaimToNewGap(moving, name, note);
        }}
      />
    </>
  );
}
